import {
  CreateTaskFromQuestionCommandInput,
  CreateTaskFromQuestionCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { taskKeys } from 'src/data/api/helios-service-ts/task/queries/keys';
import { taskService } from 'src/data/api/helios-service-ts/task/service';

/**
 * React Query mutation hook for creating a task draft from a question.
 * Uses the typed SDK client (CreateTaskFromQuestionCommand).
 * Invalidates the task list query on success to trigger a refetch.
 * No retry — mutation is not idempotent.
 *
 * @param key - Key parameters for mutation identification
 * @param options - Optional mutation options (mutationKey and mutationFn cannot be overridden)
 * @returns React Query mutation result
 */
export const useCreateTaskDraftMutation = (
  key: Pick<CreateTaskFromQuestionCommandInput, 'businessUnitId'>,
  options?: Omit<
    UseMutationOptions<
      CreateTaskFromQuestionCommandOutput,
      Error,
      CreateTaskFromQuestionCommandInput
    >,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationKey: taskKeys.create(key),
    mutationFn: (data: CreateTaskFromQuestionCommandInput) =>
      taskService.createFromQuestion(data),
    async onSuccess(data, variables, onMutateResult, context) {
      // Fire-and-forget: don't let a failed refetch corrupt the mutation success state
      queryClient.invalidateQueries({ queryKey: taskKeys.all }).catch(() => {});
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
